import { BUILDABLE_DIE_SET_PREFIXES } from './dieSetPrefixes'

/**
 * Known die-set code prefixes accepted by `buildProductCode` — the strict
 * subset of `VALIDATABLE_DIE_SET_PREFIXES` that carries dimensional digits.
 * `SPL` is intentionally absent: the special format has no dimensions to
 * encode.
 *
 * NOTE on round-trip: only the rectangular prefixes (DD, SDD, MD, SGDD) and
 * a square RD (verticalIn === horizontalIn) cleanly round-trip through
 * `parseProductCode`. `DDH-` and `RDS-` decode to `dieSetType = 'DD'` and
 * `'RD'` respectively (the parser collapses them by design). Non-square
 * `RD-` codes are rejected by the parser because the rounded format is a
 * single 4-digit diameter; we still allow callers to build the 8-digit
 * representation, but the inverse will only see the leading 4 digits.
 */
const KNOWN_DIE_SET_PREFIXES = new Set<string>(BUILDABLE_DIE_SET_PREFIXES)

/**
 * Encodes a single inch dimension into the 4-digit thousandths string
 * (e.g. `1.30` → `'1300'`) that prefixes like `DD-` / `MD-` concatenate
 * pairwise to form the dimensional portion of a product code.
 *
 * Rounds to 3 decimal places first so float drift (`1.299999…`) lands on
 * the same integer the parser would emit for the same value.
 */
const padDimensionThousandths = (valueIn: number, label: string): string => {
  if (!Number.isFinite(valueIn) || valueIn <= 0) {
    throw new Error(`buildProductCode: ${label} must be a positive number greater than 0 (received ${valueIn}).`)
  }

  const thousandths = Math.round(valueIn * 1000)

  // 4-digit pad means values < 10" only. Anything ≥ 10" would overflow the
  // 4-digit slot and silently collide with adjacent dimensions in the
  // concatenated code (e.g. 10.0" → "10000" merges into the next slot).
  if (thousandths > 9999) {
    throw new Error(
      `buildProductCode: ${label} must be less than 10 inches to fit a 4-digit code (received ${valueIn}).`
    )
  }

  return String(thousandths).padStart(4, '0')
}

/**
 * Builds a product code from die-set type and two inch dimensions. Inverse
 * of `parseProductCode` for the rectangular formats: produces
 * `{dieSetType}-{paddedVertical}{paddedHorizontal}` with each dimension
 * rounded to 3 decimals and zero-padded to 4 digits.
 *
 * Example: `buildProductCode('DD', 1.30, 1.70)` → `'DD-13001700'`.
 *
 * Throws when `dieSetType` is not in the known prefix set, or when either
 * dimension is not a finite positive number less than 10 inches.
 */
export const buildProductCode = (dieSetType: string, verticalIn: number, horizontalIn: number): string => {
  if (!KNOWN_DIE_SET_PREFIXES.has(dieSetType)) {
    throw new Error(
      `buildProductCode: unknown dieSetType "${dieSetType}". Expected one of: ${[...KNOWN_DIE_SET_PREFIXES].join(', ')}.`
    )
  }

  const paddedVertical = padDimensionThousandths(verticalIn, 'verticalIn')
  const paddedHorizontal = padDimensionThousandths(horizontalIn, 'horizontalIn')

  return `${dieSetType}-${paddedVertical}${paddedHorizontal}`
}
